'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  acceptedVideoMimeTypes,
  formatVideoDurationMs,
  useLoadedVideoSource,
} from '@/components/tools/video-tool-shared';
import {
  attachVideoProgressListener,
  blurVideoBackground,
  cancelActiveVideoWork,
  compressVideo,
  loopVideo,
  muteVideo,
  normalizeVideoToMp4,
  recoverVideoRuntimeAfterFailure,
  resizeVideo,
  rotateVideo,
  trimVideo,
  changeVideoSpeed,
  type VideoFitMode,
  convertVideoToContainer,
  getVideoContainerExtension,
  type VideoQualityPreset,
  type VideoScaleMode,
} from '@/lib/browser-video-tools';
import { clamp, formatFileSize, getBaseName, sanitizeHexColor } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { trackEvent } from '@/lib/analytics';

// The wasm heap tops out around 2 GB and MEMFS holds the input and the output in
// it at once, so past roughly this size the run aborts instead of finishing.
const MAX_VIDEO_FILE_BYTES = 1.5 * 1024 * 1024 * 1024;
const LARGE_VIDEO_NOTE_BYTES = 300 * 1024 * 1024;

export type VideoSingleToolVariant =
  | 'video-compressor'
  | 'video-resizer'
  | 'video-trimmer'
  | 'video-to-mp4-converter'
  | 'mute-video'
  | 'video-speed-changer'
  | 'rotate-video'
  | 'video-loop-maker'
  | 'video-background-blur'
  | 'mp4-to-mov'
  | 'mp4-to-mkv'
  | 'mp4-to-webm'
  | 'mp4-to-avi';

// The four container variants all share one code path (convertVideoToContainer); only
// the target container differs, so they are a lookup rather than four switch cases.
const containerVariantTargets = {
  'mp4-to-mov': 'mov',
  'mp4-to-mkv': 'mkv',
  'mp4-to-webm': 'webm',
  'mp4-to-avi': 'avi',
} as const;

type ContainerVariant = keyof typeof containerVariantTargets;

function isContainerVariant(variant: VideoSingleToolVariant): variant is ContainerVariant {
  return variant in containerVariantTargets;
}

type ToolCopy = {
  title: string;
  subtitle: string;
  badge: string;
  buttonLabel: string;
  processingLabel: string;
  successLabel: string;
};

type BackgroundMode = 'white' | 'black' | 'custom';
type QualityTier = 'standard' | 'lighter' | 'smallest';
type SpeedPreset = '0.5' | '0.75' | '1.25' | '1.5' | '2' | 'custom';
type RotatePreset = '-90' | '90' | '180';
type RatioPreset = 'square' | 'portrait' | 'landscape' | 'wide';

const toolCopy: Record<VideoSingleToolVariant, ToolCopy> = {
  'video-compressor': {
    title: 'Compress one video locally',
    subtitle:
      'Reduce MP4 size in the browser with a preset-driven export that keeps the audio track.',
    badge: 'MP4',
    buttonLabel: 'Compress video',
    processingLabel: 'Compressing…',
    successLabel: 'Video compression',
  },
  'video-resizer': {
    title: 'Resize a video for upload or social use',
    subtitle:
      'Set a target canvas, keep or crop the aspect ratio, and export one resized MP4 locally.',
    badge: 'Resize',
    buttonLabel: 'Resize video',
    processingLabel: 'Resizing…',
    successLabel: 'Video resize',
  },
  'video-trimmer': {
    title: 'Trim a video clip by start and end time',
    subtitle:
      'Pick the section you need and export a shorter MP4 without sending the video to a server.',
    badge: 'Trim',
    buttonLabel: 'Trim video',
    processingLabel: 'Trimming…',
    successLabel: 'Video trim',
  },
  'video-to-mp4-converter': {
    title: 'Convert common video formats to MP4',
    subtitle:
      'Normalize MOV, WebM, MKV, AVI, MPEG, MPG, and M4V input into a browser-ready MP4 export.',
    badge: 'Convert',
    buttonLabel: 'Convert to MP4',
    processingLabel: 'Converting…',
    successLabel: 'Video conversion',
  },
  'mp4-to-mov': {
    title: 'Convert an MP4 to a QuickTime MOV',
    subtitle: 'Re-wrap the video as MOV with H.264 and AAC, for QuickTime and Final Cut.',
    badge: 'Convert',
    buttonLabel: 'Convert to MOV',
    processingLabel: 'Converting…',
    successLabel: 'MOV conversion',
  },
  'mp4-to-mkv': {
    title: 'Convert an MP4 to a Matroska MKV',
    subtitle: 'Re-wrap the video as MKV with H.264 and AAC, for media servers and Plex.',
    badge: 'Convert',
    buttonLabel: 'Convert to MKV',
    processingLabel: 'Converting…',
    successLabel: 'MKV conversion',
  },
  'mp4-to-webm': {
    title: 'Convert an MP4 to WebM',
    subtitle: 'Export VP8 video with Vorbis audio for the web, with no upload and no size cap.',
    badge: 'Convert',
    buttonLabel: 'Convert to WebM',
    processingLabel: 'Converting…',
    successLabel: 'WebM conversion',
  },
  'mp4-to-avi': {
    title: 'Convert an MP4 to AVI',
    subtitle: 'Export MPEG-4 video with MP3 audio for older players and Windows tools.',
    badge: 'Convert',
    buttonLabel: 'Convert to AVI',
    processingLabel: 'Converting…',
    successLabel: 'AVI conversion',
  },
  'mute-video': {
    title: 'Remove the audio track from a video',
    subtitle:
      'Strip the soundtrack and export one muted MP4 locally for reels, clips, and silent demos.',
    badge: 'Mute',
    buttonLabel: 'Mute video',
    processingLabel: 'Muting…',
    successLabel: 'Muted video export',
  },
  'video-speed-changer': {
    title: 'Change video playback speed',
    subtitle: 'Speed up or slow down a clip while keeping the edit simple and fully browser-side.',
    badge: 'Speed',
    buttonLabel: 'Change speed',
    processingLabel: 'Changing speed…',
    successLabel: 'Video speed change',
  },
  'rotate-video': {
    title: 'Rotate a video into the right orientation',
    subtitle: 'Fix sideways uploads and export a corrected MP4 without opening a full editor.',
    badge: 'Rotate',
    buttonLabel: 'Rotate video',
    processingLabel: 'Rotating…',
    successLabel: 'Video rotation',
  },
  'video-loop-maker': {
    title: 'Repeat a short clip into a looped MP4',
    subtitle: 'Choose a clip range, set the repeat count, and export one loop-ready MP4 locally.',
    badge: 'Loop',
    buttonLabel: 'Make loop',
    processingLabel: 'Looping…',
    successLabel: 'Video loop export',
  },
  'video-background-blur': {
    title: 'Add blurred background fill behind a video',
    subtitle:
      'Place the source video over a blurred canvas to fit portrait, square, or landscape layouts.',
    badge: 'Blur',
    buttonLabel: 'Blur background',
    processingLabel: 'Rendering…',
    successLabel: 'Background blur export',
  },
};

function getOutputBaseName(variant: VideoSingleToolVariant, fileName: string) {
  const baseName = getBaseName(fileName);

  switch (variant) {
    case 'video-compressor':
      return `${baseName}-compressed`;
    case 'video-resizer':
      return `${baseName}-resized`;
    case 'video-trimmer':
      return `${baseName}-trimmed`;
    case 'video-to-mp4-converter':
      return `${baseName}-mp4`;
    case 'mp4-to-mov':
      return `${baseName}-mov`;
    case 'mp4-to-mkv':
      return `${baseName}-mkv`;
    case 'mp4-to-webm':
      return `${baseName}-webm`;
    case 'mp4-to-avi':
      return `${baseName}-avi`;
    case 'mute-video':
      return `${baseName}-muted`;
    case 'video-speed-changer':
      return `${baseName}-speed`;
    case 'rotate-video':
      return `${baseName}-rotated`;
    case 'video-loop-maker':
      return `${baseName}-loop`;
    case 'video-background-blur':
      return `${baseName}-blurred`;
  }
}

function applyRatioPreset(preset: RatioPreset, sourceWidth: number, sourceHeight: number) {
  const base = Math.max(64, sourceWidth || 1080);

  if (preset === 'square') {
    return { width: base, height: base };
  }

  if (preset === 'portrait') {
    return { width: 1080, height: 1920 };
  }

  if (preset === 'wide') {
    return { width: 1920, height: 1080 };
  }

  return { width: 1080, height: 1350 };
}

export default function BrowserVideoSingleFileTool({
  variant,
  pair,
  toolSlug,
}: {
  variant: VideoSingleToolVariant;
  /** Format-pair pages (vob-to-mp4 …) reuse a container variant with their own heading. */
  pair?: { from: string; to: string };
  toolSlug?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const copy: ToolCopy = pair
    ? {
        ...toolCopy[variant],
        title: `Convert ${pair.from.toUpperCase()} to ${pair.to.toUpperCase()}`,
        badge: pair.from.toUpperCase(),
      }
    : toolCopy[variant];
  const { source, loadSource, clearSource } = useLoadedVideoSource();
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressRatio, setProgressRatio] = useState<number | null>(null);
  const wasCancelledRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [preset, setPreset] = useState<VideoQualityPreset>('balanced');
  const [fpsCap, setFpsCap] = useState<'keep' | '30' | '24' | '20'>('keep');
  const [qualityTier, setQualityTier] = useState<QualityTier>('standard');
  const [targetWidth, setTargetWidth] = useState(1080);
  const [targetHeight, setTargetHeight] = useState(1080);
  const [fitMode, setFitMode] = useState<VideoFitMode>('contain');
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>('black');
  const [customBackground, setCustomBackground] = useState('#000000');
  const [startMs, setStartMs] = useState(0);
  const [endMs, setEndMs] = useState(0);
  const [speedPreset, setSpeedPreset] = useState<SpeedPreset>('1.25');
  const [customSpeed, setCustomSpeed] = useState('1.25');
  const [rotation, setRotation] = useState<RotatePreset>('90');
  const [repeatCount, setRepeatCount] = useState(3);
  const [ratioPreset, setRatioPreset] = useState<RatioPreset>('portrait');
  const [blurStrength, setBlurStrength] = useState(18);
  const [scaleMode, setScaleMode] = useState<VideoScaleMode>('contain');

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  useEffect(() => {
    if (!source) {
      return;
    }

    setStartMs(0);
    setEndMs(source.metadata.durationMs);
    setTargetWidth(source.metadata.width || 1080);
    setTargetHeight(source.metadata.height || 1080);
    const ratio = applyRatioPreset('portrait', source.metadata.width, source.metadata.height);
    setRatioPreset('portrait');
    setTargetWidth((current) =>
      variant === 'video-background-blur' ? ratio.width : current || source.metadata.width,
    );
    setTargetHeight((current) =>
      variant === 'video-background-blur' ? ratio.height : current || source.metadata.height,
    );
  }, [source, variant]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The video could not be loaded.');
    }
  }

  async function runTool() {
    if (!source) {
      setErrorMessage('Choose a video first.');
      return;
    }

    if (
      (variant === 'video-trimmer' ||
        variant === 'video-loop-maker' ||
        variant === 'video-speed-changer') &&
      endMs <= startMs
    ) {
      setErrorMessage('Set an end time that is later than the start time.');
      return;
    }

    if (source.file.size > MAX_VIDEO_FILE_BYTES) {
      setErrorMessage(
        `This file is ${formatFileSize(source.file.size)}. Browser-side processing holds the whole video in memory, so files over ${formatFileSize(MAX_VIDEO_FILE_BYTES)} cannot finish reliably. Trim or split the video first, or use a desktop tool for files this large.`,
      );
      return;
    }

    setIsProcessing(true);
    setProgressRatio(null);
    setErrorMessage(null);
    setCreditMessage(null);

    let detachProgress: (() => void) | null = null;
    wasCancelledRef.current = false;

    try {
      detachProgress = await attachVideoProgressListener((ratio) => setProgressRatio(ratio));
      const credits = await consumeToolUsage(
        toolSlug ?? variant,
        'Credits are not available for another video export right now.',
      );

      const background =
        backgroundMode === 'white'
          ? '#ffffff'
          : backgroundMode === 'black'
            ? '#000000'
            : sanitizeHexColor(customBackground);

      let outputBlob: Blob;

      switch (variant) {
        case 'video-compressor':
          outputBlob = await compressVideo({
            file: source.file,
            preset,
            fpsCap: fpsCap === 'keep' ? null : Number(fpsCap),
            qualityTier,
          });
          break;
        case 'video-resizer':
          outputBlob = await resizeVideo({
            file: source.file,
            width: targetWidth,
            height: targetHeight,
            fitMode,
            background,
          });
          break;
        case 'video-trimmer':
          outputBlob = await trimVideo({
            file: source.file,
            startMs,
            endMs,
          });
          break;
        case 'video-to-mp4-converter':
          outputBlob = await normalizeVideoToMp4({
            file: source.file,
            preset,
          });
          break;
        case 'mp4-to-mov':
        case 'mp4-to-mkv':
        case 'mp4-to-webm':
        case 'mp4-to-avi':
          outputBlob = await convertVideoToContainer({
            file: source.file,
            target: containerVariantTargets[variant],
          });
          break;
        case 'mute-video':
          outputBlob = await muteVideo(source.file);
          break;
        case 'video-speed-changer': {
          const speedFactor =
            speedPreset === 'custom'
              ? clamp(Number.parseFloat(customSpeed) || 1, 0.25, 4)
              : Number.parseFloat(speedPreset);
          outputBlob = await changeVideoSpeed({
            file: source.file,
            speedFactor,
          });
          break;
        }
        case 'rotate-video':
          outputBlob = await rotateVideo({
            file: source.file,
            rotation: Number(rotation) as -90 | 90 | 180,
          });
          break;
        case 'video-loop-maker':
          outputBlob = await loopVideo({
            file: source.file,
            startMs,
            endMs,
            repeatCount,
          });
          break;
        case 'video-background-blur':
          outputBlob = await blurVideoBackground({
            file: source.file,
            canvasWidth: targetWidth,
            canvasHeight: targetHeight,
            blurStrength,
            scaleMode,
          });
          break;
      }

      const nextOutputUrl = URL.createObjectURL(outputBlob);

      resetOutputState();
      setOutputUrl(nextOutputUrl);
      setDownloadName(
        `${getOutputBaseName(variant, source.file.name)}.${
          isContainerVariant(variant)
            ? getVideoContainerExtension(containerVariantTargets[variant])
            : 'mp4'
        }`,
      );
      setResultSize(outputBlob.size);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      if (wasCancelledRef.current) {
        setCreditMessage('Conversion cancelled.');
      } else {
        const message =
          error instanceof Error ? error.message : 'The local video export could not finish.';
        setErrorMessage(message);
        trackEvent('tool_error', { tool_slug: variant, error_message: message.slice(0, 100) });
        // A failed exec can leave the wasm core aborted; reset so the next run works
        // without a page reload.
        recoverVideoRuntimeAfterFailure();
      }
    } finally {
      detachProgress?.();
      setIsProcessing(false);
      setProgressRatio(null);
    }
  }

  function cancelRun() {
    wasCancelledRef.current = true;
    cancelActiveVideoWork();
  }

  const selectedSpeed = useMemo(
    () =>
      speedPreset === 'custom'
        ? clamp(Number.parseFloat(customSpeed) || 1, 0.25, 4)
        : Number(speedPreset),
    [customSpeed, speedPreset],
  );

  const clipDurationMs = Math.max(0, endMs - startMs);
  const estimatedLoopDurationMs = clipDurationMs * repeatCount;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      {variant === 'video-background-blur' ? (
        <div className="tool-live-routes" aria-label="Canvas presets">
          {(
            [
              ['portrait', 'Portrait 9:16'],
              ['landscape', 'Portrait feed 4:5'],
              ['square', 'Square 1:1'],
              ['wide', 'Landscape 16:9'],
            ] as const
          ).map(([presetValue, label]) => (
            <button
              key={presetValue}
              className={`tool-live-route ${ratioPreset === presetValue ? 'tool-live-route-active' : ''}`}
              onClick={() => {
                setRatioPreset(presetValue);
                const nextSize = applyRatioPreset(
                  presetValue,
                  source?.metadata.width || 1080,
                  source?.metadata.height || 1920,
                );
                setTargetWidth(nextSize.width);
                setTargetHeight(nextSize.height);
                resetOutputState();
              }}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one video or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Local browser-side video processing with MP4 output'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={acceptedVideoMimeTypes}
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {variant === 'video-compressor' ? (
            <>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Preset</span>
                  <select
                    onChange={(event) => {
                      setPreset(event.target.value as VideoQualityPreset);
                      resetOutputState();
                    }}
                    value={preset}
                  >
                    <option value="balanced">Balanced</option>
                    <option value="smaller">Smaller file</option>
                    <option value="aggressive">Aggressive</option>
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>FPS cap</span>
                  <select
                    onChange={(event) => {
                      setFpsCap(event.target.value as typeof fpsCap);
                      resetOutputState();
                    }}
                    value={fpsCap}
                  >
                    <option value="keep">Keep original</option>
                    <option value="30">30 fps</option>
                    <option value="24">24 fps</option>
                    <option value="20">20 fps</option>
                  </select>
                </label>
              </div>

              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Quality tier</span>
                  <select
                    onChange={(event) => {
                      setQualityTier(event.target.value as QualityTier);
                      resetOutputState();
                    }}
                    value={qualityTier}
                  >
                    <option value="standard">Standard</option>
                    <option value="lighter">Lighter</option>
                    <option value="smallest">Smallest</option>
                  </select>
                </label>
              </div>
            </>
          ) : null}

          {variant === 'video-resizer' ? (
            <>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Width</span>
                  <input
                    min="2"
                    onChange={(event) => {
                      setTargetWidth(Math.max(2, Number(event.target.value) || 2));
                      resetOutputState();
                    }}
                    type="number"
                    value={targetWidth}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Height</span>
                  <input
                    min="2"
                    onChange={(event) => {
                      setTargetHeight(Math.max(2, Number(event.target.value) || 2));
                      resetOutputState();
                    }}
                    type="number"
                    value={targetHeight}
                  />
                </label>
              </div>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Fit mode</span>
                  <select
                    onChange={(event) => {
                      setFitMode(event.target.value as VideoFitMode);
                      resetOutputState();
                    }}
                    value={fitMode}
                  >
                    <option value="contain">Contain</option>
                    <option value="cover">Cover</option>
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Background</span>
                  <select
                    onChange={(event) => {
                      setBackgroundMode(event.target.value as BackgroundMode);
                      resetOutputState();
                    }}
                    value={backgroundMode}
                  >
                    <option value="black">Black</option>
                    <option value="white">White</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
              </div>
              {backgroundMode === 'custom' ? (
                <label className="tool-live-field">
                  <span>Custom color</span>
                  <input
                    onChange={(event) => {
                      setCustomBackground(sanitizeHexColor(event.target.value));
                      resetOutputState();
                    }}
                    type="text"
                    value={customBackground}
                  />
                </label>
              ) : null}
            </>
          ) : null}

          {variant === 'video-trimmer' || variant === 'video-loop-maker' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Start (ms)</span>
                <input
                  min="0"
                  onChange={(event) => {
                    setStartMs(Math.max(0, Number(event.target.value) || 0));
                    resetOutputState();
                  }}
                  type="number"
                  value={startMs}
                />
              </label>
              <label className="tool-live-field">
                <span>End (ms)</span>
                <input
                  min="0"
                  onChange={(event) => {
                    setEndMs(Math.max(0, Number(event.target.value) || 0));
                    resetOutputState();
                  }}
                  type="number"
                  value={endMs}
                />
              </label>
            </div>
          ) : null}

          {variant === 'video-to-mp4-converter' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Quality preset</span>
                <select
                  onChange={(event) => {
                    setPreset(event.target.value as VideoQualityPreset);
                    resetOutputState();
                  }}
                  value={preset}
                >
                  <option value="balanced">Balanced</option>
                  <option value="smaller">Smaller file</option>
                  <option value="aggressive">Aggressive</option>
                </select>
              </label>
            </div>
          ) : null}

          {variant === 'video-speed-changer' ? (
            <>
              <div className="tool-live-routes" aria-label="Speed presets">
                {(['0.5', '0.75', '1.25', '1.5', '2', 'custom'] as const).map((value) => (
                  <button
                    key={value}
                    className={`tool-live-route ${speedPreset === value ? 'tool-live-route-active' : ''}`}
                    onClick={() => {
                      setSpeedPreset(value);
                      resetOutputState();
                    }}
                    type="button"
                  >
                    {value === 'custom' ? 'Custom' : `${value}x`}
                  </button>
                ))}
              </div>
              {speedPreset === 'custom' ? (
                <label className="tool-live-field">
                  <span>Custom multiplier</span>
                  <input
                    min="0.25"
                    max="4"
                    step="0.05"
                    onChange={(event) => {
                      setCustomSpeed(event.target.value);
                      resetOutputState();
                    }}
                    type="number"
                    value={customSpeed}
                  />
                </label>
              ) : null}
            </>
          ) : null}

          {variant === 'rotate-video' ? (
            <div className="tool-live-routes" aria-label="Rotation">
              {(
                [
                  ['-90', '90° left'],
                  ['90', '90° right'],
                  ['180', '180°'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  className={`tool-live-route ${rotation === value ? 'tool-live-route-active' : ''}`}
                  onClick={() => {
                    setRotation(value);
                    resetOutputState();
                  }}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          {variant === 'video-loop-maker' ? (
            <label className="tool-live-field">
              <span>Repeat count</span>
              <input
                min="1"
                max="20"
                onChange={(event) => {
                  setRepeatCount(clamp(Number(event.target.value) || 1, 1, 20));
                  resetOutputState();
                }}
                type="number"
                value={repeatCount}
              />
            </label>
          ) : null}

          {variant === 'video-background-blur' ? (
            <>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Canvas width</span>
                  <input
                    min="2"
                    onChange={(event) => {
                      setTargetWidth(Math.max(2, Number(event.target.value) || 2));
                      resetOutputState();
                    }}
                    type="number"
                    value={targetWidth}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Canvas height</span>
                  <input
                    min="2"
                    onChange={(event) => {
                      setTargetHeight(Math.max(2, Number(event.target.value) || 2));
                      resetOutputState();
                    }}
                    type="number"
                    value={targetHeight}
                  />
                </label>
              </div>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Blur strength</span>
                  <input
                    min="4"
                    max="40"
                    onChange={(event) => {
                      setBlurStrength(clamp(Number(event.target.value) || 18, 4, 40));
                      resetOutputState();
                    }}
                    type="number"
                    value={blurStrength}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Source scale</span>
                  <select
                    onChange={(event) => {
                      setScaleMode(event.target.value as VideoScaleMode);
                      resetOutputState();
                    }}
                    value={scaleMode}
                  >
                    <option value="contain">Contain</option>
                    <option value="cover">Cover</option>
                  </select>
                </label>
              </div>
            </>
          ) : null}

          {source && source.file.size > LARGE_VIDEO_NOTE_BYTES && !isProcessing ? (
            <p className="support-feedback">
              {`Large file (${formatFileSize(source.file.size)}): the conversion runs on this device and can take several minutes.`}
            </p>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runTool()}
              type="button"
            >
              {isProcessing
                ? progressRatio !== null && progressRatio > 0
                  ? `${copy.processingLabel} ${Math.round(progressRatio * 100)}%`
                  : copy.processingLabel
                : copy.buttonLabel}
            </button>
            {isProcessing ? (
              <button className="button button-ghost" onClick={cancelRun} type="button">
                Cancel
              </button>
            ) : null}
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                {`Download ${downloadName.split('.').pop()?.toUpperCase() ?? 'file'}`}
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Export details</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Source</strong>
              <span>
                {source
                  ? `${source.metadata.width} × ${source.metadata.height}`
                  : 'No video loaded'}
              </span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>
                {source ? formatVideoDurationMs(source.metadata.durationMs) : 'No video loaded'}
              </span>
            </div>
            <div>
              <strong>Source size</strong>
              <span>{source ? formatFileSize(source.file.size) : 'No video loaded'}</span>
            </div>
            <div>
              <strong>Result size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'No export yet'}</span>
            </div>
            {variant === 'video-compressor' ? (
              <div>
                <strong>Compression mode</strong>
                <span>{`${preset} / ${qualityTier}`}</span>
              </div>
            ) : null}
            {variant === 'video-resizer' ? (
              <div>
                <strong>Canvas</strong>
                <span>{`${targetWidth} × ${targetHeight}`}</span>
              </div>
            ) : null}
            {variant === 'video-trimmer' || variant === 'video-loop-maker' ? (
              <div>
                <strong>Selected clip</strong>
                <span>{formatVideoDurationMs(clipDurationMs)}</span>
              </div>
            ) : null}
            {variant === 'video-speed-changer' ? (
              <div>
                <strong>Speed</strong>
                <span>{selectedSpeed.toFixed(2)}x</span>
              </div>
            ) : null}
            {variant === 'video-loop-maker' ? (
              <div>
                <strong>Looped length</strong>
                <span>{formatVideoDurationMs(estimatedLoopDurationMs)}</span>
              </div>
            ) : null}
            {variant === 'video-background-blur' ? (
              <div>
                <strong>Canvas target</strong>
                <span>{`${targetWidth} × ${targetHeight}`}</span>
              </div>
            ) : null}
          </div>

          {variant === 'mute-video' ? (
            <div className="tool-live-panel-note">
              <strong>Audio</strong>
              <span>The output MP4 removes the audio track entirely.</span>
            </div>
          ) : null}

          <div className="tool-live-preview">
            <h3>Preview</h3>
            <div className="tool-live-preview-frame">
              {outputUrl ? (
                <video controls playsInline src={outputUrl} />
              ) : source ? (
                <video controls muted playsInline src={source.previewUrl} />
              ) : (
                <p>Load a video to preview it here.</p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
